/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useQuery, useMutation } from 'react-query'

import { queryClient } from '@modules/query'
import type { AuditLogsDestinationSettings, AuditLogsStatus } from '@modules/cloud-api/v1/types'
import {
  disableAuditLogsUrl,
  enableAuditLogsUrl,
  getAuditLogsStatusUrl,
} from '@modules/cloud-api/v1/organizations'
import { fetchAsJson } from '@modules/query/helpers'
import type { QueryHookOptions } from '@modules/query/types'
import type {
  DisableAuditLogsUrlParams,
  GetAuditLogsStatusUrlParams,
} from '@modules/cloud-api/v1/organizations'

export const useEnableAuditLogsConfiguration = () =>
  useMutation<
    AuditLogsStatus,
    unknown,
    { payload: AuditLogsDestinationSettings; organizationId: string }
  >(['enable-audit-logs'], {
    mutationFn: ({
      payload,
      organizationId,
    }: {
      payload: AuditLogsDestinationSettings
      organizationId: string
    }): Promise<AuditLogsStatus> =>
      fetchAsJson<AuditLogsStatus>(enableAuditLogsUrl({ organizationId }), {
        method: 'post',
        body: JSON.stringify(payload),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(['get-audit-logs', variables.organizationId])
    },
  })

export const useGetAuditLogsConfiguration = (
  params: GetAuditLogsStatusUrlParams,
  options?: QueryHookOptions,
) =>
  useQuery(
    ['get-audit-logs', params.organizationId],
    () =>
      fetchAsJson<AuditLogsStatus>(
        getAuditLogsStatusUrl({ organizationId: params.organizationId }),
        {
          method: 'get',
        },
      ),
    { ...options },
  )

export const useDisableAuditLogs = (params: DisableAuditLogsUrlParams) =>
  useMutation<AuditLogsStatus, unknown, void>(['disable-audit-logs', params.organizationId], {
    mutationFn: () =>
      fetchAsJson<AuditLogsStatus>(disableAuditLogsUrl({ organizationId: params.organizationId }), {
        method: 'delete',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['get-audit-logs', params.organizationId])
    },
  })
